// FASE 05 — S3+/PO 2026-05-13 (Q5=A): valida em tempo real, com debounce de 500ms,
// se o codigo digitado no campo Codigo do cadastro de produto N1 ja existe na empresa.
// Cada tipo de produto tem seu proprio endpoint e funcao validadora — o hook recebe
// a funcao validadora como parametro para permitir reuso entre Perfil/Acabamento/
// Componente/Kit/Superficie/Vidro.
//
// Sinais retornados:
//   - codigoDuplicado:  true quando o codigo digitado ja existe em outro registro
//                       (validacao remota retornou disponivel=false)
//   - validando:        true durante a chamada remota (apos debounce, antes da resposta)
//   - resetar():        forca limpar os flags (chamar ao trocar de registro/abrir novo)

import { useEffect, useRef, useState } from "react";

type Validador = (codigo: string, idExcluir?: number) => Promise<boolean>;

export function useValidacaoCodigoUnico(
  codigo: string | undefined,
  idAtual: number | undefined,
  validador: Validador,
  delayMs: number = 500
) {
  const [codigoDuplicado, setCodigoDuplicado] = useState(false);
  const [validando, setValidando] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ultimoCodigoValidadoRef = useRef<string | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // FASE 05 — 6ª validação/PO 2026-08-07: em EDIÇÃO (registro existente, idAtual > 0) não valida
    // duplicidade em tempo real — a mensagem "Código já existe" só deve aparecer ao CRIAR um novo
    // cadastro com código repetido. A validação no salvar (backend) continua barrando duplicata real.
    const editando = !!idAtual && idAtual > 0;
    const codigoNormalizado = (codigo || "").trim();
    if (editando || codigoNormalizado.length < 1) {
      setCodigoDuplicado(false);
      setValidando(false);
      return;
    }

    if (ultimoCodigoValidadoRef.current === codigoNormalizado) {
      return;
    }

    setValidando(true);
    timeoutRef.current = setTimeout(async () => {
      try {
        const disponivel = await validador(codigoNormalizado, idAtual);
        ultimoCodigoValidadoRef.current = codigoNormalizado;
        setCodigoDuplicado(!disponivel);
      } catch {
        setCodigoDuplicado(false);
      } finally {
        setValidando(false);
      }
    }, delayMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [codigo, idAtual, validador, delayMs]);

  const resetar = () => {
    setCodigoDuplicado(false);
    setValidando(false);
    ultimoCodigoValidadoRef.current = null;
  };

  return { codigoDuplicado, validando, resetar };
}
